import { makeStyles } from '@material-ui/core';
import { MoreHoriz } from '@material-ui/icons';
import axios from 'axios';
import { differenceInCalendarMonths, endOfDay, startOfDay } from 'date-fns';
import React from 'react';
import { ArrowLeft, Copy, Edit, Trash, Upload } from 'react-feather';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { usePrefixTranslation } from '../../../i18n';
import { actions, store, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { Area, AssetType, CostCenter } from '../../../types/asset';
import { enumToStatusType, projectStatus } from '../../../types/common';
import { ProjectSummary } from '../../../types/project';
import { Report } from '../../../types/report';
import { exportToExcel, getContentUrl } from '../../../util/file';
import { Avatar, Badge, Tabs } from '../../global';
import { Button } from '../../global/forms/Button';
import { DeleteReport } from '../dialogs/DeleteReport';
import { DuplicateReport } from '../dialogs/DuplicateReport';
import { ExportReport } from '../dialogs/ExportReport';
import { ReportEditor } from '../dialogs/ReportEditor';
import { computeMetrics, Metrics } from './Metrics';

const useReportDetailsClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        background: theme.colors.neutral[6],
    },
    tabs: {
        padding: '0 40px',
        backgroundColor: theme.colors.darkBlue[1],
        boxShadow: 'none',
    },
    tab: {
        borderRadius: '8px 8px 0 0',
        '&, &:hover': {
            backgroundColor: theme.colors.darkBlue[1],
            color: theme.colors.neutral[4],
        },
        '&.open': {
            backgroundColor: theme.colors.neutral[6],
            color: theme.colors.neutral[1],
        },
    },
    tabPane: {
        padding: 40,
    },
});

export const ReportDetails = (props: { reportId?: number; containerRef?: React.MutableRefObject<HTMLDivElement> }) => {
    const classes = useReportDetailsClasses();
    const { t } = usePrefixTranslation('report', { report: 'details.ReportDetails' });
    const navigate = useNavigate();
    const params = useParams();
    const details$ = React.useRef<HTMLDivElement>();
    const dispatch = useTypedDispatch();
    const { report } = useTypedSelector(state => ({ report: state.report }));
    const [loading, setLoading] = React.useState(true);
    const printPage = useReactToPrint({
        content: () => details$.current!,
        documentTitle: report.report?.title,
    });
    const [reportEditorOpen, setReportEditorOpen] = React.useState(false);
    const [duplicateReportOpen, setDuplicateReportOpen] = React.useState(false);
    const [deleteReportOpen, setDeleteReportOpen] = React.useState(false);
    const [exportReportOpen, setExportReportOpen] = React.useState(false);
    const [types, setTypes] = React.useState<AssetType[]>();
    const [areas, setAreas] = React.useState<Area[]>();
    const [costCenters, setCostCenters] = React.useState<CostCenter[]>();
    const [forecasted, setForecasted] = React.useState<ProjectSummary[]>([]);
    const [scheduled, setScheduled] = React.useState<ProjectSummary[]>([]);

    React.useEffect(() => {
        dispatch(actions.report.get(parseInt(params.id ?? props.reportId)));
    }, []);

    React.useEffect(() => {
        axios.get<AssetType[]>('/asset-types').then(response => setTypes(response.data));
        axios.get<Area[]>('/areas').then(response => setAreas(response.data));
        axios.get<CostCenter[]>('/cost-centers').then(response => setCostCenters(response.data));
    }, []);

    React.useEffect(() => {
        setLoading(true);
        if (!report.report || !types || !areas || !costCenters) return;
        const selectedTypes =
            report.report.asset_type_filters.length > 0
                ? new Set(types.filter(t => report.report?.asset_type_filters.includes(t.id)).map(t => t.name))
                : undefined;
        const selectedAreas =
            report.report.area_filters.length > 0
                ? new Set(areas.filter(a => report.report?.area_filters.includes(a.id)).map(a => a.name))
                : undefined;
        const selectedCostCenters =
            report.report.cost_center_filters.length > 0
                ? new Set(costCenters.filter(c => report.report?.cost_center_filters.includes(c.id)).map(c => c.name))
                : undefined;
        const forecastedPromise = axios.get<ProjectSummary[]>('/inspections/overview', {
            params: {
                pagination: 'False',
                start_date: startOfDay(report.report!.start_date),
                end_date: endOfDay(report.report!.end_date),
                inspection_plan__isnull: 'False',
            },
        });
        const scheduledPromise = axios.get<ProjectSummary[]>('/inspections/overview', {
            params: {
                pagination: 'False',
                start_date: startOfDay(report.report!.start_date),
                end_date: endOfDay(report.report!.end_date),
                status: projectStatus.join(','),
            },
        });
        Promise.all([forecastedPromise, scheduledPromise]).then(responses => {
            const forecastedProjects = responses[0].data;
            const scheduledProjects = responses[1].data;
            setForecasted(
                forecastedProjects.filter(
                    p =>
                        (!selectedTypes || selectedTypes.has(p.asset_type!)) &&
                        (!selectedAreas || selectedAreas.has(p.area_name!)) &&
                        (!selectedCostCenters || selectedCostCenters.has(p.cost_center_name!)),
                ),
            );
            setScheduled(
                scheduledProjects.filter(
                    p =>
                        (!selectedTypes || selectedTypes.has(p.asset_type!)) &&
                        (!selectedAreas || selectedAreas.has(p.area_name!)) &&
                        (!selectedCostCenters || selectedCostCenters.has(p.cost_center_name!)),
                ),
            );
            setLoading(false);
        });
    }, [report.report, types, areas, costCenters]);

    return (
        <div ref={props.containerRef ?? (details$ as any)} className={classes.root}>
            <Header
                report={report.changed}
                onEdit={() => setReportEditorOpen(true)}
                onDuplicate={() => setDuplicateReportOpen(true)}
                onDelete={() => setDeleteReportOpen(true)}
                onExport={() => setExportReportOpen(true)}
            />
            <Tabs
                tabsProps={{ className: classes.tabs }}
                tabProps={{ className: classes.tab }}
                trackerProps={{ style: { display: 'none' } }}
                paneProps={{ className: classes.tabPane }}
                labels={[t('view-metrics'), t('view-assets')]}
            >
                {[<Metrics loading={loading} report={report.report} forecasted={forecasted} scheduled={scheduled} />]}
            </Tabs>
            <ReportEditor
                key={`ReportEditor-${reportEditorOpen}`}
                loading={report.fetching || report.status === 'updating'}
                report={report.changed}
                mode='update'
                open={reportEditorOpen}
                onCancel={() => setReportEditorOpen(false)}
                onSave={async report => {
                    dispatch(actions.report.edit(report));
                    dispatch(actions.report.commit());
                    await dispatch(actions.report.update());
                    setReportEditorOpen(false);
                }}
            />
            <DuplicateReport
                key={`DuplicateReport-${duplicateReportOpen}`}
                loading={report.fetching || report.status === 'updating'}
                title={report.report?.title ?? ''}
                open={duplicateReportOpen}
                onCancel={() => setDuplicateReportOpen(false)}
                onDuplicate={async title => {
                    const duplicatedReport = { ...report.report, id: -1, title };
                    dispatch(actions.report.create());
                    dispatch(actions.report.edit({ ...duplicatedReport }));
                    dispatch(actions.report.commit());
                    await dispatch(actions.report.update());
                    setDuplicateReportOpen(false);
                    const duplicatedId = store.getState().report.report?.id;
                    navigate(`../${duplicatedId != undefined ? duplicatedId : ''}`);
                }}
            />
            <DeleteReport
                key={`DeleteReport-${deleteReportOpen}`}
                loading={report.fetching || report.status === 'updating'}
                title={report.report?.title ?? ''}
                open={deleteReportOpen}
                onCancel={() => setDeleteReportOpen(false)}
                onDelete={async () => {
                    await dispatch(actions.report.delete());
                    setDeleteReportOpen(false);
                    navigate('/reports');
                }}
            />
            <ExportReport
                key={`ExportReport-${exportReportOpen}`}
                loading={report.fetching || report.status === 'updating'}
                title={report.report?.title ?? ''}
                open={exportReportOpen}
                onCancel={() => {
                    setExportReportOpen(false);
                    dispatch(actions.report.reset());
                }}
                onExport={(format, fileName) => {
                    setExportReportOpen(false);
                    if (format === 'pdf') printPage?.();
                    if (format === 'excel') {
                        const metrics = computeMetrics(
                            report.report!,
                            forecasted,
                            scheduled,
                            {
                                start: report.report?.start_date!,
                                end: report.report?.end_date!,
                            },
                            'month',
                        );
                        const forecastedSheetData = metrics.forecasted;
                        const scheduledSheetData = metrics.scheduled;
                        const { scheduled: scheduledData, forecasted: forecastedData, ...restData } = metrics;
                        exportToExcel(
                            [
                                { name: 'Forecasted Projects', data: forecastedSheetData },
                                { name: 'Scheduled Projects', data: scheduledSheetData },
                                { name: 'Rest', data: [restData] },
                            ],
                            fileName,
                        );
                    }
                }}
            />
        </div>
    );
};

const useHeaderClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: '40px 40px 20px 40px',
        gap: 12,
        background: theme.colors.darkBlue[1],
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        '& > span': {
            color: theme.colors.neutral[3],
            ...theme.fonts.labelSemiBold,
        },
    },
    back: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        color: 'white',
        ...theme.fonts.button,
        textDecoration: 'none',
    },
    buttonMore: {
        color: 'white',
        backgroundColor: theme.colors.darkBlue[1],
        '&.open, &:hover': { backgroundColor: theme.colors.darkBlue[5] },
    },
    menu: {
        display: 'contents',
        '& > button': { justifyContent: 'flex-start' },
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        color: theme.colors.neutral[5],
        ...theme.fonts.h3,
    },
    details: {
        display: 'flex',
        alignItems: 'center',
        gap: 32,
        color: theme.colors.neutral[3],
        ...theme.fonts.label,
    },
    user: {
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        '& > div': { width: 20, height: 20 },
    },
});

const Header = (props: {
    report?: Report;
    onEdit: () => void;
    onDuplicate: () => void;
    onDelete: () => void;
    onExport: () => void;
}) => {
    const classes = useHeaderClasses();
    const { t, Trans } = usePrefixTranslation(['report', 'common'], { report: 'details.ReportDetails.Header' });
    const dispatch = useTypedDispatch();
    const { users } = useTypedSelector(state => ({ users: state.user }));
    const user = users.users.filter(u => u.id === props.report?.user)[0];
    const [picture, setPicture] = React.useState('');
    const months = differenceInCalendarMonths(props.report?.end_date ?? 0, props.report?.start_date ?? 0);
    const template = months <= 1 ? 'monthly' : months <= 3 ? 'quarterly' : 'yearly';
    const status = !props.report
        ? ''
        : +startOfDay(props.report.start_date) > +startOfDay(new Date())
        ? 'not started'
        : +startOfDay(props.report.end_date) < +startOfDay(new Date())
        ? 'completed'
        : 'in progress';

    React.useEffect(() => void dispatch(actions.user.get()), []);

    React.useEffect(() => {
        if (!user || user.picture === '') return setPicture('');
        getContentUrl(user.picture).then(url => setPicture(url));
    }, [user?.picture]);

    return (
        <div className={classes.root}>
            <div className={classes.actions}>
                <Link className={classes.back} to='../'>
                    <ArrowLeft />
                    {t('label-back')}
                </Link>
                <div style={{ flex: 1 }} />
                <span>{t('label-last-updated', { date: new Date() })}</span>
                <Button
                    className={classes.buttonMore}
                    icon
                    menu={
                        <div className={classes.menu}>
                            <Button theme='blue' onClick={props.onEdit}>
                                <Edit />
                                {t('button-edit')}
                            </Button>
                            <Button theme='normal' onClick={props.onDuplicate}>
                                <Copy />
                                {t('button-duplicate')}
                            </Button>
                            <Button theme='warning' onClick={props.onDelete}>
                                <Trash />
                                {t('button-delete')}
                            </Button>
                        </div>
                    }
                >
                    <MoreHoriz />
                </Button>
                <Button theme='primary' onClick={props.onExport}>
                    <Upload />
                    {t('button-export')}
                </Button>
            </div>

            <div className={classes.header}>
                {props.report?.title}
                <Badge type={enumToStatusType[`report:${status}`]}>{t(`common:status.${status}`)}</Badge>
            </div>
            <div className={classes.details}>
                <span>{t('label-created-date', { date: props.report?.created_date })}</span>
                {!!user && (
                    <span className={classes.user}>
                        <Trans
                            i18nKey='label-created-user'
                            values={{ name: user.full_name }}
                            components={{ avatar: <Avatar name={user.full_name} src={picture} /> }}
                        />
                    </span>
                )}
                <span>{t(`label-template-${template}`)}</span>
                <span>{t('report-interval', { start: props.report?.start_date, end: props.report?.end_date })}</span>
            </div>
        </div>
    );
};
